import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouteContext,
} from '@tanstack/react-router';

import { ImpersonationBanner } from '@/components/impersonation-banner/impersonation-banner';
import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { settingsV3QueryOptions } from '@/lib/feature-flags/settings-v3';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

import { SettingsNav } from './settings/-components/settings-nav';
import { legacySettingsUrl } from './settings/-utils/legacy-settings-url';

export const Route = createFileRoute('/researchers/_authenticated/settings')({
  beforeLoad: async ({ context, location }) => {
    const resolved = await resolveActiveDashboard({ context, location });

    const settingsV3 =
      resolved.v3Enabled === true &&
      (await context.queryClient.ensureQueryData(
        settingsV3QueryOptions(
          resolved.user.id,
          resolved.activeDashboard.organization,
          resolved.activeDashboard.team,
        ),
      ));

    if (!settingsV3) {
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0) {
        throw redirect({
          href: legacySettingsUrl(
            env.WEBSITE_URL,
            location.pathname,
            resolved.activeDashboard,
          ),
        });
      }
      throw redirect({ to: '/researchers/projects' });
    }

    return resolved;
  },
  component: SettingsLayout,
  head: () => ({ meta: [{ title: t`Account settings` }] }),
});

function SettingsLayout() {
  const { user, activeDashboard } = Route.useRouteContext();
  const { env, isAdminImpersonating, adminUser } = useRouteContext({
    from: '__root__',
  });

  return (
    <div className="flex h-screen flex-col print:block print:h-auto">
      {isAdminImpersonating && adminUser && (
        <ImpersonationBanner
          adminUser={adminUser}
          websiteUrl={env.WEBSITE_URL}
          impersonatedLabel={user.email}
        />
      )}
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        <div className="mx-auto flex w-full max-w-360 flex-col gap-6 px-6 py-8 lg:px-8">
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Settings</Trans>
          </h1>
          <div className="flex flex-col gap-8 lg:flex-row">
            <SettingsNav />
            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      </ResearcherShell>
    </div>
  );
}
